import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";

const navLinks = [
  { label: "Soportes Publicitarios", href: "#soportes" },
  { label: "Beneficios", href: "#beneficios" },
  { label: "Red de Edificios", href: "#red" },
  { label: "Sobre Nosotros", href: "#sobre" },
  { label: "Contacto", href: "#contacto" },
];

const Footer = () => (
  <footer
    data-testid="footer"
    style={{
      backgroundColor: "#050505",
      borderTop: "1px solid rgba(255,255,255,0.06)",
      paddingTop: "80px",
    }}
  >
    <div className="edi-container">
      {/* Top: Logo + tagline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7 }}
        className="mb-16"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: "48px" }}
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          {/* Logo */}
          <div>
            <div className="flex items-baseline gap-0 mb-4">
              <span
                className="font-heading font-bold text-white"
                style={{ fontSize: "clamp(36px, 5vw, 64px)", letterSpacing: "0.08em", lineHeight: 1 }}
              >
                EDIMEDIA
              </span>
              <span
                className="font-heading font-bold"
                style={{
                  fontSize: "clamp(36px, 5vw, 64px)",
                  color: "#00E0FF",
                  letterSpacing: "0.08em",
                  lineHeight: 1,
                }}
              >
                .CL
              </span>
            </div>
            <p
              className="font-body text-zinc-500"
              style={{ fontSize: "13px", fontWeight: 300, letterSpacing: "0.05em" }}
            >
              La red publicitaria residencial más grande de Chile.
            </p>
          </div>

          {/* CTA */}
          <a
            href="#contacto"
            data-testid="footer-cta"
            className="flex items-center gap-3 font-body font-bold uppercase text-xs text-black self-start"
            style={{
              backgroundColor: "#00E0FF",
              padding: "14px 28px",
              letterSpacing: "0.18em",
              transition: "background-color 0.3s",
              textDecoration: "none",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#ffffff")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#00E0FF")}
          >
            <span>Contactar</span>
            <ArrowUpRight size={13} />
          </a>
        </div>
      </motion.div>

      {/* Middle: Links + Contact */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
        {/* Navigation */}
        <div>
          <span
            className="font-body font-bold uppercase block mb-6"
            style={{ fontSize: "10px", color: "#52525b", letterSpacing: "0.25em" }}
          >
            Navegación
          </span>
          <ul className="flex flex-col gap-3">
            {navLinks.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  className="font-body text-zinc-500 hover:text-white"
                  style={{ fontSize: "13px", fontWeight: 300, transition: "color 0.3s", textDecoration: "none" }}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div>
          <span
            className="font-body font-bold uppercase block mb-6"
            style={{ fontSize: "10px", color: "#52525b", letterSpacing: "0.25em" }}
          >
            Soportes
          </span>
          <ul className="flex flex-col gap-3">
            {[
              "Exterior OOH",
              "Cierres Perimetrales",
              "Branding en Accesos",
              "Pantallas Digitales",
              "Publicidad en Azotea",
            ].map((item) => (
              <li key={item}>
                <span
                  className="font-body text-zinc-500"
                  style={{ fontSize: "13px", fontWeight: 300 }}
                >
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <span
            className="font-body font-bold uppercase block mb-6"
            style={{ fontSize: "10px", color: "#52525b", letterSpacing: "0.25em" }}
          >
            Contacto
          </span>
          <div className="flex flex-col gap-4">
            <a
              href="tel:+56991388500"
              data-testid="footer-phone"
              className="flex items-center gap-3 group"
              style={{ textDecoration: "none" }}
            >
              <Phone size={13} color="#00E0FF" />
              <span
                className="font-body text-zinc-500 group-hover:text-white"
                style={{ fontSize: "13px", fontWeight: 300, transition: "color 0.3s" }}
              >
                +56 9 9138 8500
              </span>
            </a>
            <a
              href="mailto:Contacto@Edimedia.cl"
              data-testid="footer-email"
              className="flex items-center gap-3 group"
              style={{ textDecoration: "none" }}
            >
              <Mail size={13} color="#D4AF37" />
              <span
                className="font-body text-zinc-500 group-hover:text-white"
                style={{ fontSize: "13px", fontWeight: 300, transition: "color 0.3s" }}
              >
                Contacto@Edimedia.cl
              </span>
            </a>
            <a
              href="https://maps.google.com/?q=Av+Las+Condes+7700,+Santiago,+Chile"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-address"
              className="flex items-start gap-3 group"
              style={{ textDecoration: "none" }}
            >
              <MapPin size={13} color="#00E0FF" style={{ marginTop: "1px", flexShrink: 0 }} />
              <span
                className="font-body text-zinc-500 group-hover:text-white"
                style={{ fontSize: "13px", fontWeight: 300, lineHeight: 1.6, transition: "color 0.3s" }}
              >
                Av Las Condes 7700<br />Santiago, Chile
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom: Copyright */}
      <div
        className="flex flex-col md:flex-row items-center justify-between gap-6"
        style={{
          borderTop: "1px solid rgba(255,255,255,0.05)",
          paddingTop: "28px",
          paddingBottom: "28px",
        }}
      >
        <span
          className="font-body text-zinc-700"
          style={{ fontSize: "11px", letterSpacing: "0.1em" }}
        >
          &copy; {new Date().getFullYear()} EDIMEDIA.CL — Todos los derechos reservados.
        </span>

        {/* ALS Group */}
        <div className="flex items-center gap-3">
          <span
            className="font-body text-zinc-400"
            style={{ fontSize: "11px", letterSpacing: "0.12em", whiteSpace: "nowrap" }}
          >
            Una empresa de
          </span>
          <a
            href="https://alsgroup.cl"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-alsgroup"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_rpc-chile/artifacts/sob4aaux_ALSGroup%20VS%20Inmobiliario%20%285%29.png"
              alt="ALS Group — Venture Studio Inmobiliario"
              style={{ height: "36px", width: "auto", opacity: 0.75, transition: "opacity 0.3s" }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.75")}
            />
          </a>
        </div>
      </div>
    </div>
  </footer>
);

export { Footer };
